<script setup lang="ts">
import { computed, reactive, watch } from 'vue'

import type { JsonValue, Slide, SlidePatchOperation } from '../types'

const props = defineProps<{
  slide: Slide
  busy?: boolean
}>()

const emit = defineEmits<{
  save: [ops: SlidePatchOperation[]]
}>()

interface FormModel {
  title: string
  subtitle: string
  author: string
  index: string
  body: string
  quote: string
  caption: string
  contact: string
  items: string
  leftTitle: string
  leftItems: string
  rightTitle: string
  rightItems: string
  stats: Array<{ value: string; label: string }>
  steps: Array<{ title: string; body: string }>
  notes: string
  imageAlt: string
}

const form = reactive<FormModel>({
  title: '',
  subtitle: '',
  author: '',
  index: '',
  body: '',
  quote: '',
  caption: '',
  contact: '',
  items: '',
  leftTitle: '',
  leftItems: '',
  rightTitle: '',
  rightItems: '',
  stats: [],
  steps: [],
  notes: '',
  imageAlt: '',
})

function text(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

function lines(value: unknown): string {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string').join('\n')
    : ''
}

function hydrate(): void {
  const content = props.slide.content
  form.title = text(content.title)
  form.subtitle = text(content.subtitle)
  form.author = text(content.author)
  form.index = text(content.index)
  form.body = text(content.body)
  form.quote = text(content.quote)
  form.caption = text(content.caption)
  form.contact = text(content.contact)
  form.items = lines(content.items)

  const left = content.left && typeof content.left === 'object'
    ? content.left as Record<string, unknown>
    : {}
  const right = content.right && typeof content.right === 'object'
    ? content.right as Record<string, unknown>
    : {}
  form.leftTitle = text(left.title)
  form.leftItems = lines(left.items)
  form.rightTitle = text(right.title)
  form.rightItems = lines(right.items)

  form.stats = Array.isArray(content.stats)
    ? content.stats.map((item) => {
        const stat = item && typeof item === 'object' ? item as Record<string, unknown> : {}
        return { value: text(stat.value), label: text(stat.label) }
      })
    : []
  form.steps = Array.isArray(content.steps)
    ? content.steps.map((item) => {
        const step = item && typeof item === 'object' ? item as Record<string, unknown> : {}
        return { title: text(step.title), body: text(step.body) }
      })
    : []
  form.notes = props.slide.notes
  form.imageAlt = props.slide.image?.alt ?? ''
}

watch(() => props.slide, hydrate, { immediate: true, deep: true })

function parseLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

const validationError = computed(() => {
  const layout = props.slide.layout
  if (['title', 'section', 'bullets', 'text', 'steps', 'two_cols', 'closing'].includes(layout)) {
    if (!form.title.trim()) return 'Добавьте заголовок.'
  }
  if (layout === 'title' && form.title.trim().length > 70) {
    return 'На титульном слайде заголовок — не длиннее 70 знаков.'
  }
  if (layout === 'section' && form.title.trim().length > 40) {
    return 'Заголовок раздела — не длиннее 40 знаков.'
  }
  if (layout === 'bullets') {
    const items = parseLines(form.items)
    if (items.length < 2 || items.length > 6) return 'Нужно от 2 до 6 тезисов.'
    if (items.some((item) => item.length > 90)) return 'Каждый тезис — не длиннее 90 знаков.'
  }
  if (layout === 'text') {
    if (!form.body.trim()) return 'Добавьте основной текст.'
    if (form.body.trim().length > 420) return 'Основной текст — не длиннее 420 знаков.'
  }
  if (layout === 'quote') {
    if (!form.quote.trim()) return 'Добавьте цитату.'
    if (form.quote.trim().length > 220) return 'Цитата — не длиннее 220 знаков.'
  }
  if (layout === 'stats') {
    if (form.stats.length < 2 || form.stats.length > 4) return 'Нужно от 2 до 4 показателей.'
    if (form.stats.some((stat) => !stat.value.trim() || !stat.label.trim())) {
      return 'Заполните число и подпись у каждого показателя.'
    }
    if (form.stats.some((stat) => stat.value.trim().length > 8 || stat.label.trim().length > 40)) {
      return 'Число — до 8 знаков, подпись — до 40.'
    }
  }
  if (layout === 'two_cols') {
    const left = parseLines(form.leftItems)
    const right = parseLines(form.rightItems)
    if (!form.leftTitle.trim() || !form.rightTitle.trim()) return 'Назовите обе колонки.'
    if (left.length < 1 || left.length > 4 || right.length < 1 || right.length > 4) {
      return 'В каждой колонке должно быть от 1 до 4 пунктов.'
    }
  }
  if (layout === 'steps') {
    if (form.steps.length < 2 || form.steps.length > 5) return 'Нужно от 2 до 5 шагов.'
    if (form.steps.some((step) => !step.title.trim())) return 'Назовите каждый шаг.'
    if (form.steps.some((step) => step.title.trim().length > 40)) {
      return 'Название шага — не длиннее 40 знаков.'
    }
  }
  if (layout === 'closing' && !form.title.trim()) return 'Добавьте финальную фразу.'
  return ''
})

function addStat(): void {
  if (form.stats.length < 4) form.stats.push({ value: '', label: '' })
}

function addStep(): void {
  if (form.steps.length < 5) form.steps.push({ title: '', body: '' })
}

function makeOperation(path: string, value: JsonValue): SlidePatchOperation {
  return { op: 'replace', slideId: props.slide.id, path, value }
}

function slideHasPath(path: string): boolean {
  const segments = path.split('.')
  let current: unknown = props.slide
  for (const segment of segments) {
    if (
      !current
      || typeof current !== 'object'
      || Array.isArray(current)
      || !Object.prototype.hasOwnProperty.call(current, segment)
    ) return false
    current = (current as Record<string, unknown>)[segment]
  }
  return true
}

function optionalOperation(
  operations: SlidePatchOperation[],
  path: string,
  value: string,
): void {
  if (value.trim()) operations.push(makeOperation(path, value.trim()))
  else if (slideHasPath(path)) {
    operations.push({ op: 'remove', slideId: props.slide.id, path })
  }
}

function submit(): void {
  if (validationError.value) return
  const operations: SlidePatchOperation[] = []
  const layout = props.slide.layout

  if (['title', 'section', 'bullets', 'text', 'two_cols', 'steps', 'closing'].includes(layout)) {
    operations.push(makeOperation('content.title', form.title.trim()))
  }
  if (layout === 'title') {
    optionalOperation(operations, 'content.subtitle', form.subtitle)
    optionalOperation(operations, 'content.author', form.author)
  } else if (layout === 'section') {
    optionalOperation(operations, 'content.index', form.index)
  } else if (layout === 'bullets') {
    operations.push(makeOperation('content.items', parseLines(form.items)))
  } else if (layout === 'text') {
    operations.push(makeOperation('content.body', form.body.trim()))
  } else if (layout === 'quote') {
    operations.push(makeOperation('content.quote', form.quote.trim()))
    optionalOperation(operations, 'content.author', form.author)
  } else if (layout === 'stats') {
    optionalOperation(operations, 'content.title', form.title)
    operations.push(makeOperation(
      'content.stats',
      form.stats.map((stat) => ({ value: stat.value.trim(), label: stat.label.trim() })),
    ))
  } else if (layout === 'two_cols') {
    operations.push(
      makeOperation('content.left.title', form.leftTitle.trim()),
      makeOperation('content.left.items', parseLines(form.leftItems)),
      makeOperation('content.right.title', form.rightTitle.trim()),
      makeOperation('content.right.items', parseLines(form.rightItems)),
    )
  } else if (layout === 'steps') {
    operations.push(makeOperation(
      'content.steps',
      form.steps.map((step) => ({
        title: step.title.trim(),
        ...(step.body.trim() ? { body: step.body.trim() } : {}),
      })),
    ))
  } else if (layout === 'image') {
    optionalOperation(operations, 'content.title', form.title)
    optionalOperation(operations, 'content.caption', form.caption)
  } else if (layout === 'closing') {
    optionalOperation(operations, 'content.subtitle', form.subtitle)
    optionalOperation(operations, 'content.contact', form.contact)
  }
  operations.push(makeOperation('notes', form.notes))
  if (props.slide.image?.source === 'upload') {
    optionalOperation(operations, 'image.alt', form.imageAlt)
  }
  emit('save', operations)
}
</script>

<template>
  <form class="slide-form" @submit.prevent="submit">
    <div v-if="['title', 'section', 'bullets', 'text', 'stats', 'two_cols', 'steps', 'closing', 'image'].includes(slide.layout)" class="field">
      <label for="slide-title">
        {{ slide.layout === 'closing' ? 'Финальная фраза' : 'Заголовок' }}
      </label>
      <input
        id="slide-title"
        v-model="form.title"
        type="text"
        :maxlength="slide.layout === 'title' ? 70 : slide.layout === 'section' ? 40 : 220"
        placeholder="Коротко и по делу"
      >
    </div>

    <template v-if="slide.layout === 'title'">
      <div class="field">
        <label for="slide-subtitle">Подзаголовок</label>
        <textarea id="slide-subtitle" v-model="form.subtitle" rows="2" placeholder="Что зритель поймёт из этой презентации"></textarea>
      </div>
      <div class="field">
        <label for="slide-author">Автор</label>
        <input id="slide-author" v-model="form.author" type="text" placeholder="Имя или команда">
      </div>
    </template>

    <div v-else-if="slide.layout === 'section'" class="field">
      <label for="slide-index">Номер или метка раздела</label>
      <input id="slide-index" v-model="form.index" type="text" placeholder="02">
    </div>

    <div v-else-if="slide.layout === 'bullets'" class="field">
      <label for="slide-items">Тезисы <small>каждый с новой строки</small></label>
      <textarea id="slide-items" v-model="form.items" rows="7" placeholder="Первый тезис&#10;Второй тезис"></textarea>
    </div>

    <div v-else-if="slide.layout === 'text'" class="field">
      <label for="slide-body">Основной текст <small>{{ form.body.length }}/420</small></label>
      <textarea id="slide-body" v-model="form.body" rows="8" maxlength="420" placeholder="Раскройте одну мысль"></textarea>
    </div>

    <template v-else-if="slide.layout === 'quote'">
      <div class="field">
        <label for="slide-quote">Цитата <small>{{ form.quote.length }}/220</small></label>
        <textarea id="slide-quote" v-model="form.quote" rows="6" maxlength="220"></textarea>
      </div>
      <div class="field">
        <label for="quote-author">Автор цитаты</label>
        <input id="quote-author" v-model="form.author" type="text">
      </div>
    </template>

    <div v-else-if="slide.layout === 'stats'" class="repeat-fields">
      <div v-for="(stat, index) in form.stats" :key="index" class="repeat-row">
        <input v-model="stat.value" maxlength="8" :aria-label="`Показатель ${index + 1}`" placeholder="42%">
        <input v-model="stat.label" maxlength="40" :aria-label="`Подпись ${index + 1}`" placeholder="рост за год">
        <button
          type="button"
          class="icon-button"
          :disabled="form.stats.length <= 2"
          aria-label="Удалить показатель"
          @click="form.stats.splice(index, 1)"
        >×</button>
      </div>
      <button v-if="form.stats.length < 4" type="button" class="text-button" @click="addStat">
        + Добавить показатель
      </button>
    </div>

    <template v-else-if="slide.layout === 'two_cols'">
      <div class="split-fields">
        <div>
          <div class="field">
            <label for="left-title">Левая колонка</label>
            <input id="left-title" v-model="form.leftTitle" type="text" placeholder="Было">
          </div>
          <div class="field">
            <label for="left-items">Пункты</label>
            <textarea id="left-items" v-model="form.leftItems" rows="5"></textarea>
          </div>
        </div>
        <div>
          <div class="field">
            <label for="right-title">Правая колонка</label>
            <input id="right-title" v-model="form.rightTitle" type="text" placeholder="Стало">
          </div>
          <div class="field">
            <label for="right-items">Пункты</label>
            <textarea id="right-items" v-model="form.rightItems" rows="5"></textarea>
          </div>
        </div>
      </div>
    </template>

    <div v-else-if="slide.layout === 'steps'" class="repeat-fields">
      <div v-for="(step, index) in form.steps" :key="index" class="step-row">
        <span>{{ index + 1 }}</span>
        <div>
          <input v-model="step.title" maxlength="40" :aria-label="`Название шага ${index + 1}`" placeholder="Название шага">
          <textarea v-model="step.body" rows="2" :aria-label="`Описание шага ${index + 1}`" placeholder="Короткое пояснение"></textarea>
        </div>
        <button
          type="button"
          class="icon-button"
          :disabled="form.steps.length <= 2"
          aria-label="Удалить шаг"
          @click="form.steps.splice(index, 1)"
        >×</button>
      </div>
      <button v-if="form.steps.length < 5" type="button" class="text-button" @click="addStep">
        + Добавить шаг
      </button>
    </div>

    <div v-else-if="slide.layout === 'image'" class="field">
      <label for="slide-caption">Подпись к изображению</label>
      <textarea id="slide-caption" v-model="form.caption" rows="3"></textarea>
    </div>

    <template v-else-if="slide.layout === 'closing'">
      <div class="field">
        <label for="closing-subtitle">Подзаголовок</label>
        <textarea id="closing-subtitle" v-model="form.subtitle" rows="2"></textarea>
      </div>
      <div class="field">
        <label for="closing-contact">Контакт или ссылка</label>
        <input id="closing-contact" v-model="form.contact" type="text">
      </div>
    </template>

    <div v-if="slide.image?.source === 'upload'" class="field">
      <label for="image-alt">Описание картинки <small>для доступности</small></label>
      <input id="image-alt" v-model="form.imageAlt" type="text">
    </div>

    <div class="field field--notes">
      <label for="slide-notes">
        Заметки докладчика
        <small>видны только на телефоне-пульте</small>
      </label>
      <textarea id="slide-notes" v-model="form.notes" rows="6" placeholder="Что важно сказать на этом слайде…"></textarea>
    </div>

    <p v-if="validationError" class="form-error">{{ validationError }}</p>
    <button type="submit" class="button button--primary button--wide" :disabled="busy || Boolean(validationError)">
      <span v-if="busy" class="mini-loader"></span>
      {{ busy ? 'Сохраняем…' : 'Сохранить текст' }}
    </button>
  </form>
</template>
